// src/storage/stationProgression.ts — 10-Tier Station Progression & Mastery Storage (FR-PROG-001)
// Tracks Set 1..10 progression, star ratings (1-3 stars), Victorian mastery seals,
// and set unlocking across all Clawey 11+ learning stations.

import { ExplorerPointsManager } from './explorerPoints';
import { StreakManager } from './streakManager';

export type MasterySealTier = 'bronze' | 'silver' | 'gold';

export interface SetCompletionRecord {
  setNumber: number;
  completedAt: number;
  score: number;
  maxScore: number;
  stars: 1 | 2 | 3;
  masterySeal: MasterySealTier;
}

export interface StationProgressionData {
  stationId: string;
  currentSet: number; // 1 to 10
  maxUnlockedSet: number; // 1 to 10
  completedSets: Record<number, SetCompletionRecord>;
  totalAttempts: number;
  lastPracticedAt: number;
}

export interface SetCompletionResult {
  newlyCompleted: boolean;
  setNumber: number;
  stars: 1 | 2 | 3;
  masterySeal: MasterySealTier;
  earnedEp: number;
  nextSetUnlocked: number | null;
}

const STORAGE_PREFIX = 'clawey_station_progression_';
const PROGRESSION_CHANGE_EVENT = 'clawey:progression-change';

export class StationProgression {
  private static listeners: Array<() => void> = [];

  private static getStorageKey(stationId: string): string {
    return `${STORAGE_PREFIX}${stationId}`;
  }

  /** Load progression for a specific station */
  static getProgression(stationId: string): StationProgressionData {
    try {
      const raw = localStorage.getItem(this.getStorageKey(stationId));
      if (raw) {
        const parsed = JSON.parse(raw);
        return {
          stationId,
          currentSet: parsed.currentSet || 1,
          maxUnlockedSet: parsed.maxUnlockedSet || 1,
          completedSets: parsed.completedSets || {},
          totalAttempts: parsed.totalAttempts || 0,
          lastPracticedAt: parsed.lastPracticedAt || 0,
        };
      }
    } catch {
      // Fallback on corrupt JSON
    }

    return {
      stationId,
      currentSet: 1,
      maxUnlockedSet: 1,
      completedSets: {},
      totalAttempts: 0,
      lastPracticedAt: 0,
    };
  }

  /** Save progression data */
  private static saveProgression(data: StationProgressionData): void {
    try {
      localStorage.setItem(this.getStorageKey(data.stationId), JSON.stringify(data));
      this.notifyListeners();
    } catch (err) {
      console.warn('Failed to persist station progression:', err);
    }
  }

  /** Check if a given set is unlocked for a station */
  static isSetUnlocked(stationId: string, setNumber: number): boolean {
    if (setNumber === 1) return true;
    const prog = this.getProgression(stationId);
    return setNumber <= prog.maxUnlockedSet;
  }

  /** Change Tudor's active selected set */
  static selectSet(stationId: string, setNumber: number): boolean {
    const prog = this.getProgression(stationId);
    if (setNumber < 1 || setNumber > 10) return false;
    if (setNumber > prog.maxUnlockedSet) return false;

    prog.currentSet = setNumber;
    this.saveProgression(prog);
    return true;
  }

  /**
   * Complete a set, evaluate stars (1-3), assign Victorian seal tier,
   * award Explorer Points, and unlock next set if applicable.
   */
  static completeSet(
    stationId: string,
    setNumber: number,
    score: number,
    maxScore: number
  ): SetCompletionResult {
    const prog = this.getProgression(stationId);
    const ratio = maxScore > 0 ? score / maxScore : 1;

    // Star thresholds: >=90% -> 3 stars, >=70% -> 2 stars, otherwise 1 star
    let stars: 1 | 2 | 3 = 1;
    if (ratio >= 0.9) {
      stars = 3;
    } else if (ratio >= 0.7) {
      stars = 2;
    }

    // Seal tier: Bronze (Sets 1-3), Silver (Sets 4-7), Gold (Sets 8-10)
    let masterySeal: MasterySealTier = 'bronze';
    if (setNumber >= 8) {
      masterySeal = 'gold';
    } else if (setNumber >= 4) {
      masterySeal = 'silver';
    }

    const wasAlreadyCompleted = Boolean(prog.completedSets[setNumber]);
    const existing = prog.completedSets[setNumber];

    // Only update existing if new score/stars is higher or equal
    if (!existing || stars >= existing.stars || score >= existing.score) {
      prog.completedSets[setNumber] = {
        setNumber,
        completedAt: Date.now(),
        score,
        maxScore,
        stars,
        masterySeal,
      };
    }

    prog.totalAttempts += 1;
    prog.lastPracticedAt = Date.now();

    // Unlock next set if within 1..10
    let nextSetUnlocked: number | null = null;
    if (setNumber < 10 && setNumber >= prog.maxUnlockedSet) {
      prog.maxUnlockedSet = Math.min(10, setNumber + 1);
      nextSetUnlocked = prog.maxUnlockedSet;
      // Auto-advance current set to next unlocked
      prog.currentSet = prog.maxUnlockedSet;
    }

    this.saveProgression(prog);
    StreakManager.recordActivity();

    // Award Explorer Points (+25 for completing a set, bonus for 3 stars)
    let earnedEp = 25;
    if (stars === 3) earnedEp += 15;
    if (!wasAlreadyCompleted) {
      ExplorerPointsManager.award('harder_tier', `set_${stationId}_${setNumber}`);
    }

    return {
      newlyCompleted: !wasAlreadyCompleted,
      setNumber,
      stars,
      masterySeal,
      earnedEp,
      nextSetUnlocked,
    };
  }

  /** Get count of completed sets for a station */
  static getCompletedSetCount(stationId: string): number {
    const prog = this.getProgression(stationId);
    return Object.keys(prog.completedSets).length;
  }

  /**
   * Get overall Victorian Seal for a station based on total sets mastered:
   * None: 0 sets
   * Bronze: 1-3 sets
   * Silver: 4-7 sets
   * Gilded Gold: 8-10 sets
   */
  static getStationMasterySeal(stationId: string): {
    tier: MasterySealTier | 'none';
    label: string;
    icon: string;
    completedCount: number;
    maxUnlockedSet: number;
    currentSet: number;
  } {
    const prog = this.getProgression(stationId);
    const count = Object.keys(prog.completedSets).length;

    let tier: MasterySealTier | 'none' = 'none';
    let label = 'Uncharted Waters';
    let icon = '🧭';

    if (count >= 8) {
      tier = 'gold';
      label = 'Gilded Gold Mastery';
      icon = '🥇';
    } else if (count >= 4) {
      tier = 'silver';
      label = 'Silver Naturalist Seal';
      icon = '🥈';
    } else if (count >= 1) {
      tier = 'bronze';
      label = 'Bronze Apprentice Seal';
      icon = '🥉';
    }

    return {
      tier,
      label,
      icon,
      completedCount: count,
      maxUnlockedSet: prog.maxUnlockedSet,
      currentSet: prog.currentSet,
    };
  }

  /** Listen for progression state updates across stations */
  static onProgressionChange(listener: () => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  private static notifyListeners(): void {
    this.listeners.forEach((l) => {
      try {
        l();
      } catch (e) {
        console.error('Error in station progression listener:', e);
      }
    });
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(PROGRESSION_CHANGE_EVENT));
    }
  }
}
